import React, { useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  ClickAwayListener,
  Collapse,
  alpha,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";
import { BRIEFING_STATUS_COLORS } from "../common/StatusChips";
import { getStatusLabel } from "../../utils/briefingPermissions";

type BriefingStatus = components["schemas"]["BriefingStatus"];

// Orden del flujo: decide si un cambio es hacia delante o hacia atrás.
// ARCHIVED queda fuera: es una salida del flujo, no un paso.
const FLOW_ORDER: Partial<Record<BriefingStatus, number>> = {
  DRAFT: 0,
  READY_FOR_PRICING: 1,
  READY_FOR_PRODUCTION: 2,
  IN_PRODUCTION: 3,
  UPDATING: 4,
  FINALIZED: 4,
};

// Alto del selector cerrado y abierto: al abrirse crece para que cada opción
// muestre siempre si devuelve o avanza.
const CLOSED_HEIGHT = 40;
const OPEN_HEIGHT = 52;
const EASE = "cubic-bezier(0.2, 0, 0, 1)";

/** Color de un estado sacado de la paleta del tema (el mismo que el chip). */
export const briefingStatusColor = (
  theme: Theme,
  status: BriefingStatus
): string => {
  const key = BRIEFING_STATUS_COLORS[status];
  if (!key || key === "default") return theme.palette.grey[500];
  return theme.palette[key].main;
};

const appear = keyframes`
  from { opacity: 0; transform: translateX(var(--from-x)) scale(0.9); }
  to { opacity: 1; transform: none; }
`;

const Dot: React.FC<{ color: string }> = ({ color }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 8,
      height: 8,
      borderRadius: "50%",
      flexShrink: 0,
      bgcolor: color,
    }}
  />
);

/** Texto pequeño en mayúsculas sobre o junto al nombre del estado. */
const captionSx = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  lineHeight: 1,
} as const;

interface OptionProps {
  status: BriefingStatus;
  direction: "back" | "forward" | "archive";
  index: number;
  onSelect: (status: BriefingStatus) => void;
}

const StatusOption: React.FC<OptionProps> = ({
  status,
  direction,
  index,
  onSelect,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const color =
    direction === "archive"
      ? theme.palette.error.main
      : briefingStatusColor(theme, status);
  const arrowSx = {
    fontSize: 13,
    transition: "transform 0.2s ease",
  };
  const caption =
    direction === "back"
      ? t("briefingsView.statusSwitch.sendBack", "Send back")
      : direction === "forward"
        ? t("briefingsView.statusSwitch.moveForward", "Move forward")
        : t("briefingsView.statusSwitch.exitFlow", "Exit flow");
  const label =
    direction === "archive"
      ? t("briefingsView.statusSwitch.archive", "Archive")
      : getStatusLabel(status);
  return (
    <ButtonBase
      onClick={() => onSelect(status)}
      sx={{
        "--from-x": direction === "back" ? "12px" : "-12px",
        animation: `${appear} 0.28s ${EASE} both`,
        animationDelay: `${80 + index * 45}ms`,
        display: "inline-flex",
        flexDirection: "column",
        alignItems: direction === "back" ? "flex-start" : "flex-end",
        justifyContent: "center",
        gap: 0.5,
        height: OPEN_HEIGHT - 8,
        px: 1.75,
        borderRadius: 999,
        border: `1px dashed ${alpha(color, 0.55)}`,
        whiteSpace: "nowrap",
        color: "text.primary",
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        "&:hover, &:focus-visible": {
          bgcolor: alpha(color, 0.14),
          borderStyle: "solid",
          borderColor: color,
          "& .arrow-back": { transform: "translateX(-3px)" },
          "& .arrow-forward": { transform: "translateX(3px)" },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          ...captionSx,
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          color: "text.secondary",
        }}
      >
        {direction === "back" && (
          <ArrowBackRoundedIcon className="arrow-back" sx={arrowSx} />
        )}
        {caption}
        {direction === "forward" && (
          <ArrowForwardRoundedIcon className="arrow-forward" sx={arrowSx} />
        )}
      </Box>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          fontSize: 13,
          fontWeight: 600,
          lineHeight: 1.1,
        }}
      >
        {direction === "archive" ? (
          <Inventory2OutlinedIcon sx={{ fontSize: 14, color }} />
        ) : (
          <Dot color={color} />
        )}
        {label}
      </Box>
    </ButtonBase>
  );
};

interface BriefingStatusSwitcherProps {
  status: BriefingStatus;
  /** Estados a los que el usuario puede mover el briefing. */
  transitions: BriefingStatus[];
  disabled?: boolean;
  loading?: boolean;
  onChange: (status: BriefingStatus) => void;
}

/**
 * Estado del briefing y su cambio en un mismo sitio. Al pulsarlo se abre hacia
 * los lados: a la izquierda los estados anteriores (devolver), a la derecha
 * los siguientes (avanzar) y, separado, archivar.
 */
const BriefingStatusSwitcher: React.FC<BriefingStatusSwitcherProps> = ({
  status,
  transitions,
  disabled,
  loading,
  onChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const color = briefingStatusColor(theme, status);
  const textColor = theme.palette.getContrastText(color);
  const canChange = transitions.length > 0 && !disabled;

  // Al cambiar de estado (o perder permisos) se cierra.
  useEffect(() => {
    setOpen(false);
  }, [status, canChange]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const current = FLOW_ORDER[status] ?? 0;
  const byFlow = (a: BriefingStatus, b: BriefingStatus) =>
    (FLOW_ORDER[a] ?? 0) - (FLOW_ORDER[b] ?? 0);
  const back = transitions
    .filter((s) => s !== "ARCHIVED" && (FLOW_ORDER[s] ?? 0) < current)
    .sort(byFlow);
  const forward = transitions
    .filter((s) => s !== "ARCHIVED" && (FLOW_ORDER[s] ?? 0) >= current)
    .sort(byFlow);
  const canArchive = transitions.includes("ARCHIVED");

  const select = (next: BriefingStatus) => {
    setOpen(false);
    onChange(next);
  };

  // "Status" en reposo; "Change status" y el icono de abrir al pasar el ratón
  // (y mientras está abierto).
  const active = canChange && (open ? "&" : "&:hover, &:focus-visible");
  const currentPill = (
    <ButtonBase
      disabled={!canChange || loading}
      onClick={() => setOpen((o) => !o)}
      aria-expanded={open}
      aria-haspopup="true"
      aria-label={t("briefingsView.changeStatus", "Change status")}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        height: open ? OPEN_HEIGHT - 8 : CLOSED_HEIGHT,
        px: 2,
        borderRadius: 999,
        bgcolor: color,
        color: textColor,
        fontSize: 14,
        fontWeight: 700,
        whiteSpace: "nowrap",
        flexShrink: 0,
        transition: `height 0.28s ${EASE}, box-shadow 0.2s ease, transform 0.2s ease`,
        "&.Mui-disabled": { color: textColor },
        "& .caption-idle, & .caption-active": {
          ...captionSx,
          opacity: 0.75,
        },
        "& .caption-active": { display: "none" },
        "& .expand-icon": {
          width: 0,
          opacity: 0,
          transition: `width 0.2s ${EASE}, opacity 0.2s ease`,
        },
        ...(active && {
          [active]: {
            "& .caption-idle": { display: "none" },
            "& .caption-active": { display: "inline" },
            "& .expand-icon": { width: 18, opacity: 1 },
          },
          "&:hover": { boxShadow: `0 0 0 4px ${alpha(color, 0.25)}` },
          "&:active": { transform: "scale(0.97)" },
        }),
      }}
    >
      <span className="caption-idle">
        {t("briefings.table.status", "Status")}
      </span>
      <span className="caption-active">
        {t("briefingsView.changeStatus", "Change status")}
      </span>
      {getStatusLabel(status)}
      {loading ? (
        <CircularProgress size={14} sx={{ color: textColor }} />
      ) : (
        // Abierto no lleva icono: se cierra pulsando el estado, fuera o con Esc.
        canChange &&
        !open && (
          <Box
            component="span"
            className="expand-icon"
            sx={{ display: "inline-flex", overflow: "hidden" }}
          >
            <UnfoldMoreRoundedIcon
              sx={{ fontSize: 18, transform: "rotate(90deg)" }}
            />
          </Box>
        )
      )}
    </ButtonBase>
  );

  const side = (
    items: BriefingStatus[],
    direction: "back" | "forward",
    extra?: React.ReactNode
  ) => (
    <Collapse
      orientation="horizontal"
      in={open}
      timeout={280}
      unmountOnExit
      sx={{ flexShrink: 0 }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          ...(direction === "back" ? { pr: 0.75 } : { pl: 0.75 }),
        }}
      >
        {items.map((s, i) => (
          <StatusOption
            key={s}
            status={s}
            direction={direction}
            // La animación sale desde el estado actual hacia fuera.
            index={direction === "back" ? items.length - 1 - i : i}
            onSelect={select}
          />
        ))}
        {extra}
      </Box>
    </Collapse>
  );

  return (
    <ClickAwayListener onClickAway={() => open && setOpen(false)}>
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          maxWidth: "100%",
          overflowX: "auto",
          overflowY: "hidden",
          boxSizing: "border-box",
          // Alto explícito para que el crecer al abrirse (y lo que empuja de la
          // tarjeta) vaya animado: las opciones aparecen ya con su alto.
          // 10 = relleno (4 + 4) y borde (1 + 1).
          height: (open ? OPEN_HEIGHT - 8 : CLOSED_HEIGHT) + 10,
          p: 0.5,
          m: -0.5,
          borderRadius: 999,
          border: "1px solid",
          borderColor: open ? alpha(color, 0.35) : "transparent",
          bgcolor: open ? alpha(color, 0.06) : "transparent",
          transition: `height 0.28s ${EASE}, background-color 0.28s ease, border-color 0.28s ease`,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box sx={{ display: "inline-flex", order: 1 }}>{currentPill}</Box>
        <Box sx={{ display: "inline-flex", order: 0 }}>
          {back.length > 0 && side(back, "back")}
        </Box>
        <Box sx={{ display: "inline-flex", order: 2 }}>
          {(forward.length > 0 || canArchive) &&
            side(
              forward,
              "forward",
              canArchive && (
                <>
                  {forward.length > 0 && (
                    <Box
                      aria-hidden
                      sx={{
                        width: "1px",
                        height: 26,
                        mx: 0.5,
                        bgcolor: "divider",
                      }}
                    />
                  )}
                  <StatusOption
                    status="ARCHIVED"
                    direction="archive"
                    index={forward.length}
                    onSelect={select}
                  />
                </>
              )
            )}
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

export default BriefingStatusSwitcher;
